<template>
  <div>
    <ProfileTopPanel
      v-if="manager"
      userTypeName="Менеджер"
      :name="fullName(manager)"
      :city="manager.city"
      :phone="manager.phone"
      :email="manager.email"
      :company="manager.company"
      permssionEdit="edit_manager"
      permssionDelete="пока уберем кнопку delete_manager"
      whoDelete="менеджера"
      @edit="onEdit"
    />

    <div class="cabinet-head">
      <div>
        <h2 class="cabinet-head__title">Мои остатки и кандидаты</h2>
        <div v-if="subtitle" class="cabinet-head__subtitle">{{ subtitle }}</div>
      </div>
      <div class="cabinet-head__actions">
        <v-btn outlined color="primary" :to="{ name: 'list-services' }">Каталог услуг</v-btn>
        <v-btn v-if="canSend" color="primary" @click="$emit('open-tab', 'candidates')">
          <v-icon left>mdi-send-outline</v-icon>
          Отправить тест
        </v-btn>
      </div>
    </div>

    <div class="cabinet-tiles">
      <div class="cabinet-tile">
        <div class="cabinet-tile__label">Мой остаток</div>
        <div class="cabinet-tile__value">
          <template v-if="balanceState === 'ok'">{{ totalBalance }} <small>шт.</small></template>
          <template v-else>—</template>
        </div>
      </div>
      <div v-if="receivedTotal !== null" class="cabinet-tile">
        <div class="cabinet-tile__label">Получено за всё время</div>
        <div class="cabinet-tile__value">{{ receivedTotal }}</div>
      </div>
      <div v-if="spentTotal !== null" class="cabinet-tile">
        <div class="cabinet-tile__label">Израсходовано за всё время</div>
        <div class="cabinet-tile__value">{{ spentTotal }}</div>
      </div>
      <div v-if="spentLastMonth !== null" class="cabinet-tile">
        <div class="cabinet-tile__label">Израсходовано за 30 дней</div>
        <div class="cabinet-tile__value">{{ spentLastMonth }}</div>
      </div>
      <div v-if="inProgressCount !== null" class="cabinet-tile">
        <div class="cabinet-tile__label">Кандидатов в процессе</div>
        <div class="cabinet-tile__value">{{ inProgressCount }}</div>
      </div>
    </div>

    <div class="cabinet-block mt-4">
      <h3 class="cabinet-block__title">Мои тесты</h3>
      <StateBlock v-if="balanceState === 'loading'" state="loading" compact />
      <StateBlock v-else-if="balanceState === 'error'" state="error" compact @retry="loadBalance" />
      <StateBlock v-else-if="!services.length" state="empty" compact title="Остатков по тестам пока нет" />
      <ManagerTestsTable v-else :items="services" :with-counters="hasCounters" />
    </div>

    <div class="mt-6">
      <h3 class="cabinet-block__title">Услуги из каталога</h3>
      <ListServices embedded not-bought-only />
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <div class="cabinet-block">
          <div class="d-flex align-center">
            <h3 class="cabinet-block__title">Мои последние кандидаты</h3>
            <v-spacer />
            <a v-if="canOpenCandidatesTab" class="text-primary caption" @click="$emit('open-tab', 'candidates')">Все →</a>
          </div>
          <StateBlock v-if="candidatesState === 'loading'" state="loading" compact />
          <StateBlock v-else-if="candidatesState === 'error'" state="error" compact @retry="loadCandidates" />
          <StateBlock v-else-if="!candidates.length" state="empty" compact title="Кандидатов пока нет" />
          <table v-else class="cabinet-table">
            <tr v-for="item in candidates" :key="item.id">
              <td>
                <router-link
                  v-if="canViewCandidates"
                  class="text-primary"
                  :to="{ name: 'candidates-id', params: { id: item.id } }"
                >{{ fullName(item) }}</router-link>
                <span v-else>{{ fullName(item) }}</span>
              </td>
              <td class="cabinet-table__num">{{ statusTitle(item.testing_status) }}</td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <ManagerEditDialog />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import api from '@/common/api';
import ProfileTopPanel from '@/components/common/ProfileTopPanel';
import ManagerEditDialog from '@/components/managers/ManagerEditDialog';
import StateBlock from '@/components/ui/StateBlock.vue';
import ListServices from '@/views/listServices/listServices.vue';
import ManagerTestsTable from '@/components/managers/profile/ManagerTestsTable.vue';
import { TESTING_STATUSES, TESTING_STATUSES_TITLES } from '@/common/constants/testingStatuses';

const RECENT_LIMIT = 5;

// «Мой кабинет» менеджера корпа (ТЗ, прототип): только свой остаток и свои кандидаты.
// Обороты за период и данные коллег сервер менеджеру не отдаёт — плитки без данных не показываем.
export default {
  name: 'ManagerCabinet',
  components: {
    ProfileTopPanel, ManagerEditDialog, StateBlock, ListServices, ManagerTestsTable,
  },
  props: {
    userId: { type: [Number, String], required: true },
  },
  provide() {
    return {
      userType: 'Manager',
      userId: this.userId,
    };
  },
  data() {
    return {
      stock: null,
      balanceState: 'loading', // loading | ok | error
      candidates: [],
      candidatesState: 'loading', // loading | ok | error
      inProgressCount: null,
    };
  },
  computed: {
    ...mapGetters({ loadedManager: 'users/managers/manager', me: 'auth/user' }),
    // Получено и израсходовано по тестам: счётчики менеджера из get-me (null — сервер не отдал)
    counters() {
      return (this.me && this.me.licences_received_spent) || {};
    },
    hasCounters() {
      return Object.keys(this.counters).length > 0;
    },
    // В store может лежать менеджер, открытый ранее: показываем только своего.
    manager() {
      const item = this.loadedManager;
      return item && String(item.id) === String(this.userId) ? item : null;
    },
    subtitle() {
      if (!this.manager) return '';
      return [this.fullName(this.manager), this.manager.company].filter(Boolean).join(' · ');
    },
    canSend() {
      return this.$store.getters['auth/isAllowed']('bookmark_candidates');
    },
    // Вкладка «Кандидаты» в кабинете открыта по тому же праву, что раньше пункт меню
    canOpenCandidatesTab() {
      return this.canSend;
    },
    canViewCandidates() {
      return this.$store.getters['auth/isAllowed']('view_candidate');
    },
    services() {
      const list = this.stock && Array.isArray(this.stock.balance_detailed) ? this.stock.balance_detailed : [];
      return list
        .map((item) => ({
          code: item.code,
          name: item.name,
          amount: Number(item.amount) || 0,
          received: Number((this.counters[item.code] || {}).licenses_received) || 0,
          spent: Number((this.counters[item.code] || {}).licenses_spent) || 0,
        }))
        .sort((a, b) => b.amount - a.amount);
    },
    totalBalance() {
      return this.services.reduce((sum, item) => sum + item.amount, 0);
    },
    receivedTotal() {
      return this.hasCounters ? this.services.reduce((sum, item) => sum + item.received, 0) : null;
    },
    spentTotal() {
      return this.hasCounters ? this.services.reduce((sum, item) => sum + item.spent, 0) : null;
    },
    // Плитка только если сервер отдал данные по всем услугам.
    spentLastMonth() {
      const list = this.stock && Array.isArray(this.stock.balance_detailed) ? this.stock.balance_detailed : [];
      if (!list.length || list.some((item) => item.licenses_spent_last_month == null)) return null;
      return list.reduce((sum, item) => sum + (Number(item.licenses_spent_last_month) || 0), 0);
    },
  },
  methods: {
    ...mapActions({
      loadManager: 'users/managers/loadManager',
      setDialogEditShow: 'users/managers/setDialogEditShow',
      getStockBalance: 'users/counters/getStockBalance',
    }),
    ...mapMutations({ setEditedManager: 'users/managers/setEditedManager' }),
    fullName(item) {
      return [item.last_name, item.first_name].filter(Boolean).join(' ') || 'Имя не задано';
    },
    statusTitle(status) {
      return (status && TESTING_STATUSES_TITLES[status]) || status || '—';
    },
    onEdit() {
      this.setEditedManager({ ...this.manager });
      this.setDialogEditShow(true);
    },
    async loadBalance() {
      this.balanceState = 'loading';
      try {
        const response = await this.getStockBalance({ toType: 'manager', toId: this.userId });
        // Ошибку сервера action не бросает, а возвращает тело ответа об ошибке — без balance_detailed это сбой.
        if (!response || !Array.isArray(response.balance_detailed)) throw new Error('no balance');
        this.stock = response;
        this.balanceState = 'ok';
      } catch (e) {
        this.balanceState = 'error';
      }
    },
    async loadCandidates() {
      this.candidatesState = 'loading';
      try {
        const response = await api.get('/users/candidates/', { params: { page: 1, page_size: RECENT_LIMIT } });
        this.candidates = Array.isArray(response) ? response.slice(0, RECENT_LIMIT) : (response.results || []);
        this.candidatesState = 'ok';
      } catch (e) {
        this.candidatesState = 'error';
      }
    },
    // Число кандидатов «в процессе»: если фильтр не сработал, плитку не показываем, а не рисуем ноль.
    async loadInProgress() {
      try {
        const response = await api.post(
          '/users/candidates/filter/?page=1&page_size=1',
          { testing_status: [TESTING_STATUSES.IN_PROGRESS] },
        );
        this.inProgressCount = Number.isFinite(response && response.count) ? response.count : null;
      } catch (e) {
        this.inProgressCount = null;
      }
    },
  },
  created() {
    this.loadManager(this.userId).catch(() => {});
    this.loadBalance();
    this.loadCandidates();
    this.loadInProgress();
  },
};
</script>

<style scoped lang="scss">
.cabinet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 24px 0 16px;

  &__title {
    font-size: 22px;
    font-weight: 600;
  }

  &__subtitle {
    font-size: 14px;
    color: #757575;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.cabinet-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.cabinet-tile {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  &__label {
    font-size: 13px;
    color: #757575;
  }

  &__value {
    margin-top: 6px;
    font-size: 26px;
    font-weight: 600;
    color: #212121;

    small {
      font-size: 13px;
      font-weight: 400;
      color: #757575;
    }
  }
}

.cabinet-block {
  height: 100%;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  &__title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
}

.cabinet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    text-align: left;
    font-weight: 400;
    color: #757575;
    padding: 4px 0;
  }

  td {
    padding: 8px 0;
    border-top: 1px solid #f2f2f2;
  }

  &__num {
    text-align: right !important;
    white-space: nowrap;
  }

  &__hint {
    font-size: 12px;
    color: #9e9e9e;
  }
}
</style>
